import groq from 'groq';
import { ComponentType, EntityType, MiscTypes } from '../types';
import {
  AccordionItemComponentFragment,
  PageHierarchyItemsFragment,
  IconStackComponentPropertiesFragment,
  ImageComponentFragment,
  VideoComponentFragment,
  MediaComponentFragment,
  TextComponentFragment,
  QuoteComponentFragment,
  CarouselFragment
} from './common';

const CardNewsPostFragment = groq`
  ...,
  summary,
  name,
  publicationInfo,
  tags[]->,
  poster {
    _type,  
    asset->{
      _id,
      _type,
      altText,
      description,
      metadata {
        dimensions {
          ...
        }
      }
    }
  },
`;

export const CardContentFragment = groq`
  cardContent {
    includeInAutomatedCards,
    content {
      brands,
      headline,
      summary,
      publicationDate,
      tags[]->,
      ${ImageComponentFragment}
    }
  }
`;

export const NewsCardFragment = groq`
  ...,
  defined(content) => {
    content->{
      _id,
      _type,
      slug,
      _type == "${EntityType.NewsPost}" => {
        ${CardNewsPostFragment}
      },
      _type == "${EntityType.Page}" => {
        ${PageHierarchyItemsFragment},
        name,
        ${CardContentFragment}
      },
      _type == "${MiscTypes.Document}" => {
        name,
        ${CardContentFragment}
      },
      _type == "${MiscTypes.ExternalContent}" => {
        name,
        url,
        ${CardContentFragment}
      }
    }
  }
`;

export const NewsSectionFragment = groq`
    ...,
    tags[]->,
    layout {
      ...,
      gridItems[]{
        ...,
        component->{
          ...,
          "itemId": select(
            defined(^.id) => ^.id,
            _id),
          _type == "${ComponentType.NewsCard}" => {
            ${NewsCardFragment}
          },
        }
      }
    }
`;

export const GetNewsLandingPageQuery = groq`
*[_type == "${EntityType.NewsLandingPage}"]{
    ...,
    sections[]->{${NewsSectionFragment}},
  }[0]
`;

export const GetNewsLandingSeoQuery = groq`
*[_type == "${EntityType.NewsLandingPage}"]{
    seoAttributes
  }[0]
`;

export const GetNewsSecondaryNav = groq`*[_type == "${EntityType.NewsSecondaryNav}"]{
  ...,
  items[]{
    text,
    spriteSheet,
    linkReference->
  },
  navLink{
    ...,
    "link": link->link
  },
}[0]`;

export const NewsPostContentFragment = groq`
  layout{
    ...,
    gridItems[]{
      ...,
      component->{
        ...,
        "itemId": select(
          defined(^.id) => ^.id,
          _id),
        defined(body) => {
          ${TextComponentFragment}
        },
        _type == "${ComponentType.Accordion}" => {
          ...,
          items[]-> {
            ${AccordionItemComponentFragment}
          }
        },
        _type == "${ComponentType.IconStack}" => {
          ${IconStackComponentPropertiesFragment}
        },
        _type == "${ComponentType.Quote}" => {
          ${QuoteComponentFragment}
        },
        _type == "${ComponentType.Media}" => {
          ${MediaComponentFragment}
        },
        _type == "${ComponentType.Carousel}" => {
          ${CarouselFragment}
        },
        ${ImageComponentFragment},
        ${VideoComponentFragment}
      }
    }
  }
`;

export const GetNewsPost = groq`*[_type == "${EntityType.NewsPost}" && slug.current == $slug]{
  ...,
  ${NewsPostContentFragment},
  poster {
    _type, 
    asset->{
      _id,
      _type,
      altText,
      description,
      metadata {
        dimensions {
          ...
        }
      }
    }
  },
  tags[]->
}[0]`;

export const GetNewsPostById = groq`
  *[_type == "${EntityType.NewsPost}" && _id == $id]{
    ...,
    ${NewsPostContentFragment},
    poster {
      _type,
      asset->{
        _id,
        _type,
        altText,
        description,
        metadata {
          dimensions {
            ...
          }
        }
      }
    },
    tags[]->
  }[0]
`;

export const GetNewsPostsByTagQuery = groq`
  *[_type == "${EntityType.NewsPost}" && $id in tags[]._ref]{
    ...,
    tags[]->,
    poster {
      _type, 
      asset->{
        _id,
        _type,
        altText,
        description,
        metadata {
          dimensions {
            ...
          }
        }
      }
    },
    "sortDate": coalesce(publicationInfo.publicationDate, _updatedAt),
  } | order(sortDate desc, _id desc)
`;

export const GetNewsPostsByBrandQuery = groq`
  *[_type == "${EntityType.NewsPost}" && ($brand in brands)]{
    ...,
    "sortDate": coalesce(publicationInfo.publicationDate, _updatedAt),
    poster {
      _type, 
      asset->{
        _id,
        _type,
        altText,
        description,
        metadata {
          dimensions {
            ...
          }
        }
      }
    }
  } | order(sortDate desc, _id desc)
`;

export const GetLatestPostsQuery = groq`
  *[_type == "${EntityType.NewsPost}" && tags[0]._ref in $tags && !(_id in $ids)]{
    ...,
    poster {
      _type, 
      asset->{
        _id,
        _type,
        altText,
        description,
        metadata {
          dimensions {
            ...
          }
        }
      }
    },
    "sortDate": coalesce(publicationInfo.publicationDate, _updatedAt),
    tags[]->
  } | order(sortDate desc, _id desc)[0...$limit]
`;

export const GetNewsTagsQuery = groq`*[_type == "${EntityType.NewsTag}"]`;

export const GetNewsTagQuery = groq`*[_type == "${EntityType.NewsTag}" && slug.current == $slug][0]`;

export const GetModalQuery = groq`
  *[_type == "${ComponentType.Modal}" && _id == $id]{
    ...,
    layout {
      ...,
      gridItems[]{
        ...,
        component->{
          ...,
          "itemId": select(
            defined(^.id) => ^.id,
            _id),
          ${ImageComponentFragment},
          ${VideoComponentFragment}
        }
      }
    }
  }[0]
`;

export const GetAutomatedCardsQuery = groq`
  *[
    (_type == "${EntityType.NewsPost}" && ((length($tags) == 0) || count((tags[]._ref)[@ in $tags]) > 0) && !(_id in $ids)) || 
    (_type in ["${MiscTypes.Document}", "${EntityType.Page}", "${MiscTypes.ExternalContent}"] && ((length($tags) == 0) || (count((cardContent.content.tags[]._ref)[@ in $tags]) > 0)) && cardContent.includeInAutomatedCards && !(_id in $ids))
  ]{
    _id,
    _type,
    slug,
    "sortDate": select(
      defined(publicationInfo) => publicationInfo.publicationDate,
      defined(cardContent) => cardContent.content.publicationDate,
      _updatedAt
    ),
    _type == "${EntityType.NewsPost}" => {
      ${CardNewsPostFragment}
    },
    _type == "${EntityType.Page}" => {
      ${PageHierarchyItemsFragment},
      name,
      ${CardContentFragment}
    },
    _type == "${MiscTypes.Document}" => {
      name,
      ${CardContentFragment}
    },
    _type == "${MiscTypes.ExternalContent}" => {
      name,
      url,
      ${CardContentFragment}
    }
  } | order(sortDate desc, _id desc)[0...$limit]
`;

export const GetAutomatedCardsQueryWithPages = groq`
  *[
    (_type == "${EntityType.NewsPost}" && ((length($tags) == 0) || count((tags[]._ref)[@ in $tags]) > 0) && !(_id in $ids)) || 
    (_type in ["${MiscTypes.Document}", "${EntityType.Page}", "${MiscTypes.ExternalContent}"] && ((length($tags) == 0) || (count((cardContent.content.tags[]._ref)[@ in $tags]) > 0)) && cardContent.includeInAutomatedCards && !(_id in $ids))
  ]{
    _id,
    _type,
    "sortDate": select(
      defined(publicationInfo) => publicationInfo.publicationDate,
      defined(cardContent) => cardContent.content.publicationDate,
      _updatedAt
    ),
    _type == "${EntityType.NewsPost}" => {
      slug,
      ${CardNewsPostFragment}
    },
    _type == "${EntityType.Page}" => {
      slug,
      ${PageHierarchyItemsFragment},
      name,
      ${CardContentFragment}
    },
    _type == "${MiscTypes.Document}" => {
      slug,
      name,
      ${CardContentFragment}
    },
    _type == "${MiscTypes.ExternalContent}" => {
      name,
      url,
      ${CardContentFragment}
    }
  } | order(sortDate desc, _id desc)[0...$limit]
`;

export const GetNewsCardById = groq`
  *[_type == "${ComponentType.NewsCard}" && _id == $id]{
    ${NewsCardFragment}
  }[0]  
`;

export const GetNewsSectionById = groq`
  *[_type == "${ComponentType.NewsSection}" && _id == $id]{
   ${NewsSectionFragment}
  }[0]
`;

export const GetTopicPageQuery = groq`
  *[_type == "${EntityType.NewsTopicPage}" && topic->slug.current == $slug]{
    ...,
    topic->,
    sections[]->{
      ${NewsSectionFragment}
    }
  }[0]
`;

export const GetTopicPageById = groq`
 *[_type == "${EntityType.NewsTopicPage}" && _id == $id]{
    ...,
    topic->,
    sections[]->{
      ${NewsSectionFragment}
    }
  }[0]
`;
